/**
 * The `__` data tables: the estimate, its alternates and assemblies, GCGR line items and bid
 * tabs, written to hidden sheets the user can reveal. Unlike the other report generators these
 * are deliberately unformatted — the raw tables *are* the deliverable, so customers can build
 * their own reports against them. Offered on the Reports surface under the `other` group.
 */
import Logger from "../../clients/sentry";
import {
  fetchEmployees,
  fetchGCGRDetailGroups,
  fetchGCGRExport,
  fetchGCGRNonstaffLaborTypes,
  fetchPositions,
  fetchUseGroups,
  fetchUsersByIds,
  fetchVMCategoriesByProjectId,
} from "../../api";
import { track } from "../../clients/mixpanel";
import { ValidationError, handleError, serializeErrorForWarning } from "../../errors";
import {
  Bid,
  BidGroup,
  InternalEstimateMarkup,
  ScopeAlternate,
  ScopeAlternateBid,
  ScopeTradeCleanupBid,
  ScopeUseGroup,
} from "../../interfaces/BidTabs";
import { EstimateData } from "../../interfaces/EstimateData";
import { GCGR, GCGRLineItem, StaffDetail } from "../../interfaces/GCGRs";
import { OverlineQuantitiesMap } from "../../interfaces/OverlineTotals";
import { SortCode } from "../../interfaces/SortCode";
import { SortField } from "../../interfaces/SortField";
import { UseGroup } from "../../interfaces/UseGroup";
import { User } from "../../interfaces/User";
import { getAlternateCreatorIds, transformAlternates } from "../datasources/alternates";
import { getBidTabs, transformBidTabsToFlatRows } from "../datasources/bidTabs";
import { getOverlineQuantities, getProcessedEstimate, type ProcessedEstimate } from "../datasources/estimate";
import { transformFloorAreas } from "../datasources/floorAreas";
import { transformGcgrLineItems, transformStaffDetails } from "../datasources/gcgr";
import { transformLaborCrews } from "../datasources/laborCrews";
import { SortCodeLabelJunctionMap } from "../datasources/sortCodes";
import { transformUseGroups } from "../datasources/useGroups";
import { getShowDataSheetsState, loadDataIntoSheet, toggleDataWorksheets } from "../workbook";
import { createFunctions } from "../workbook/customFunctions";
import {
  CONTRACTING_DATA_TABLES,
  DATA_TABLES,
  ESTIMATE_DATA_TABLES,
  GCGR_DATA_TABLES,
  WorkbookTable,
} from "../workbook/tableNames";

/* -------------------------------------------------------------------------- */
/* Clearing                                                                  */
/* -------------------------------------------------------------------------- */

// The data table worksheets each datasource writes, grouped so a datasource can be cleared without
// touching another's numbers. Split rather than one list because GCGR and contracting will want their
// own groups once they have cards of their own.

/**
 * Empties a data table's rows but keeps its sheet and headers, so a user's own formulas pointing at it
 * read blank rather than #REF!. loadDataIntoSheet's empty-rows path no-ops when the sheet is absent,
 * so this is safe to call on a workbook that never loaded data tables.
 */
const clearDataTables = async (tableNames: readonly WorkbookTable[]): Promise<void> => {
  // Sequential: each call opens its own Excel.run and Office.js cannot run those concurrently.
  for (const tableName of tableNames) {
    await loadDataIntoSheet([], tableName);
  }
  // That path hides each sheet it empties, which would take the tables away from a user who asked to see
  // them — and we tell them the tables are kept. Restore what they chose.
  await toggleDataWorksheets(getShowDataSheetsState());
};

/** Every datasource's tables. Shared by the teardowns so the two can't drift apart. */
export const clearAllDataTables = (): Promise<void> => clearDataTables(DATA_TABLES);

export const clearEstimateDataTables = (): Promise<void> => clearDataTables(ESTIMATE_DATA_TABLES);

export const clearGcgrDataTables = (): Promise<void> => clearDataTables(GCGR_DATA_TABLES);

export const clearContractingDataTables = (): Promise<void> => clearDataTables(CONTRACTING_DATA_TABLES);

/* -------------------------------------------------------------------------- */
/* Estimate                                                                  */
/* -------------------------------------------------------------------------- */

const getAllEstimateData = async (estimateId: string): Promise<ProcessedEstimate | undefined> => {
  const result = await getProcessedEstimate(estimateId);
  if (!result) return undefined;

  const { usedSortFields, sortFieldColumnNameMap, sortCodeLabelJunctionMap, labelSortFieldColumnNameMap } = result;
  const overlineQuantitiesMap = await getOverlineQuantities(result);

  await tabulateSortFields(
    usedSortFields,
    overlineQuantitiesMap,
    sortFieldColumnNameMap,
    sortCodeLabelJunctionMap,
    labelSortFieldColumnNameMap
  );

  return result;
};

export const loadEstimateData = async (estimateId: string): Promise<EstimateData | undefined> => {
  const startTime = performance.now();

  const response = await getAllEstimateData(estimateId);
  // Defensive only: getProcessedEstimate throws on every failure path, so a load that gets here has
  // data. Callers must catch rather than read the return to tell whether the estimate loaded.
  if (!response) {
    console.error("Error fetching estimate data");
    return;
  }
  const { baseBidItems, alternateItems, childItems, baseBidRowFills, alternateRowFills, childRowFills, estimateData } =
    response;
  await loadDataIntoSheet(baseBidItems, "Estimate", {
    sizeColumns: true,
    rowBackgroundFills: baseBidRowFills,
  });
  await loadDataIntoSheet(alternateItems, "AlternateItems", {
    sizeColumns: true,
    rowBackgroundFills: alternateRowFills,
  });
  await loadDataIntoSheet(childItems, "AssemblyItems", {
    sizeColumns: true,
    rowBackgroundFills: childRowFills,
  });
  await tabulateAlternates(estimateData);
  await tabulateUseGroups(estimateData);
  await tabulateAttributes(estimateData);
  await tabulateFloors(estimateData);
  await tabulateLaborCrews(estimateData);

  const endTime = performance.now();
  const totalDuration = (endTime - startTime) / 1000;
  track("excel.estimate.load", { loadTime: totalDuration });

  try {
    await createFunctions();
  } catch (error) {
    Logger.warn(
      "Error creating custom sheet formulas, NOTE: GSF formula fails if there is no area",
      serializeErrorForWarning(error)
    );
  }
  console.log("All data loaded into the workbook.");
  return estimateData;
};

const tabulateSortFields = async (
  sortFieldData: { [key: string]: SortField },
  overlineQuantitiesMap: OverlineQuantitiesMap,
  sortFieldColumnNameMap: Record<string, string>,
  sortCodeLabelJunctionMap: SortCodeLabelJunctionMap = new Map(),
  labelSortFieldColumnNameMap: Record<string, string> = {}
) => {
  try {
    const safeNames = new Set(Object.values(sortFieldColumnNameMap));

    const records = Object.entries(sortFieldData).flatMap(([sortFieldId, sortField]) =>
      Object.values(sortField.sort_codes as { [key: string]: SortCode }).map((sortCode) => {
        const safeName = sortFieldColumnNameMap[sortFieldId] || sortField.name;
        const sortCodeAndName = sortCode.code + " - " + sortCode.description;

        const quantity = overlineQuantitiesMap[safeName]?.[sortCodeAndName]?.["__quantity__"] ?? null;
        const uom = overlineQuantitiesMap[safeName]?.[sortCodeAndName]?.["__uom__"] ?? null;

        return {
          sort_field_id: sortField.id,
          sort_field_key: sortField.key,
          sort_field_name: sortField.name,
          sort_code_id: sortCode.id,
          sort_code_code: sortCode.code,
          sort_code_description: sortCode.description,
          quantity,
          uom,
        };
      })
    );

    // Add label sort field entries from the junction map
    const labelRecordsSeen = new Set<string>();
    sortCodeLabelJunctionMap.forEach(({ code, description }, key) => {
      const sortFieldId = key.split("::")[1];
      const labelSafeName = labelSortFieldColumnNameMap[sortFieldId];
      if (!labelSafeName) return;
      const dedupeKey = `${sortFieldId}::${code}::${description}`;
      if (labelRecordsSeen.has(dedupeKey)) return;
      labelRecordsSeen.add(dedupeKey);

      const sortField = sortFieldData[sortFieldId];
      const sortCodeAndName = `${code}${code && description ? " - " : ""}${description}`;
      const quantity = overlineQuantitiesMap[labelSafeName]?.[sortCodeAndName]?.["__quantity__"] ?? null;
      const uom = overlineQuantitiesMap[labelSafeName]?.[sortCodeAndName]?.["__uom__"] ?? null;

      records.push({
        sort_field_id: sortFieldId,
        sort_field_key: sortField?.key || "",
        sort_field_name: `${sortField?.name || labelSafeName} - Labels`,
        sort_code_id: "",
        sort_code_code: code,
        sort_code_description: description,
        quantity,
        uom,
      });
    });

    const nestedSorts = Object.entries(overlineQuantitiesMap).flatMap(([key, details]) => {
      if (
        !safeNames.has(key) &&
        typeof details === "object" &&
        details !== null &&
        (key.slice(0, 2) === "mf" || key.slice(0, 2) === "uf")
      ) {
        return Object.keys(details).map((fullKey) => {
          const [code, desc] = fullKey.split(" - ");
          const nestedDetails = details[fullKey];
          return {
            sort_field_id: "",
            sort_field_key: "",
            sort_field_name: key,
            sort_code_id: "",
            sort_code_code: code,
            sort_code_description: desc,
            quantity:
              typeof nestedDetails === "object" && nestedDetails !== null
                ? (nestedDetails["__quantity__"] ?? null)
                : null,
            uom:
              typeof nestedDetails === "object" && nestedDetails !== null ? (nestedDetails["__uom__"] ?? null) : null,
          };
        });
      }
      return [];
    });
    records.push(...nestedSorts);
    await loadDataIntoSheet(records, "SortFields");
  } catch (error) {
    Logger.warn("Error with SORT FIELDS etl", serializeErrorForWarning(error));
  }
};

const tabulateUseGroups = async (estimateData: EstimateData) => {
  try {
    if (
      !estimateData ||
      !estimateData.estimate ||
      !estimateData.estimate.stats ||
      !estimateData.estimate.stats.useGroups
    ) {
      throw new ValidationError("Invalid or missing useGroups data in estimateData.");
    }

    const useGroupsResult = await fetchUseGroups();
    let useGroupsCategoryDictionary: Record<string, string> = {};
    if (useGroupsResult.success && useGroupsResult.data) {
      useGroupsCategoryDictionary = useGroupsResult.data.reduce(
        (acc: { [key: string]: string }, useGroup: UseGroup) => ({
          ...acc,
          [useGroup.id]: useGroup.category,
        }),
        {} as Record<string, string>
      );
    } else if (!useGroupsResult.success && useGroupsResult.error) {
      // EXCEPTION: Logger error allowed here — swallowed on purpose, since use-group categories are
      // not critical to the report, but a repeated failure is still worth seeing.
      const err = handleError(useGroupsResult.error, { table: "use_groups", context: "tabulateUseGroups" });
      Logger.error("Error fetching use groups", err);
    }

    const useGroups = transformUseGroups(
      estimateData.estimate.stats.useGroups,
      estimateData.floor_groups || [],
      useGroupsCategoryDictionary
    );

    // Pass the transformed data into loadDataIntoSheet
    await loadDataIntoSheet(useGroups, "UseGroups");
  } catch (error) {
    Logger.warn("Error with USE GROUPS etl", serializeErrorForWarning(error));
  }
};

const tabulateAlternates = async (estimateData: EstimateData) => {
  try {
    const { alternates } = estimateData;

    const vmCategoryResult = await fetchVMCategoriesByProjectId(estimateData.projectId);
    const vmCategories = vmCategoryResult.success && vmCategoryResult.data ? vmCategoryResult.data : [];

    const userIds = getAlternateCreatorIds(alternates);
    let users: User[] = [];
    if (userIds.length > 0) {
      const data = await fetchUsersByIds(userIds);
      users = data.success && data.data ? data.data : [];
    }

    const updatedAlternates = transformAlternates(alternates, vmCategories, users);
    await loadDataIntoSheet(updatedAlternates, "Alternates");
  } catch (error) {
    Logger.warn("Error with ALTERNATES etl", serializeErrorForWarning(error));
  }
};

const tabulateAttributes = async (estimateData: EstimateData) => {
  try {
    if (!estimateData || !estimateData.estimate_attributes) {
      Logger.warn("No estimate attributes found");
      await loadDataIntoSheet([], "Attributes");
      return;
    }

    // Extract id, name, and value from each attribute
    const attributes = (estimateData.estimate_attributes as any[]).map((attr) => ({
      id: attr.id || "",
      name: attr.name || "",
      value: attr.value || "",
    }));

    await loadDataIntoSheet(attributes, "Attributes");
  } catch (error) {
    Logger.warn("Error with ATTRIBUTES etl", serializeErrorForWarning(error));
  }
};

const tabulateFloors = async (estimateData: EstimateData) => {
  try {
    const { area_matrix_items, area_matrix_item_estimate_use_groups, estimate_use_groups, floor_groups } = estimateData;
    const finalFloors = transformFloorAreas(
      area_matrix_items,
      area_matrix_item_estimate_use_groups,
      estimate_use_groups,
      floor_groups
    );
    await loadDataIntoSheet(finalFloors, "FloorAreas");
  } catch (error) {
    Logger.warn("Error with FLOORS etl", serializeErrorForWarning(error));
  }
};

const tabulateLaborCrews = async (estimateData: EstimateData) => {
  try {
    await loadDataIntoSheet(transformLaborCrews(estimateData), "LaborCrews");
  } catch (error) {
    Logger.warn("Error with LABOR CREWS etl", serializeErrorForWarning(error));
  }
};

/* -------------------------------------------------------------------------- */
/* GCGR                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Loads one GCGR's staffing, line-item and labor detail into the workbook's data tables. The GCGR
 * is chosen by the caller — this owns the fetching and tabulation, never the selection.
 */
export const loadGCGRData = async (projectId: string, gcgr: GCGR): Promise<void> => {
  const startTime = performance.now();
  const fullGCGRRes = await fetchGCGRExport(projectId, gcgr.id);
  if (!fullGCGRRes.success) {
    throw fullGCGRRes.error;
  }
  // An empty export payload must not count as a successful load — tabulating from it would
  // wipe the GCGR staffing/line-item tables and stamp last_updated, masking the loss
  if (!fullGCGRRes.data) {
    throw new ValidationError("GCGR export returned no data");
  }

  const staffData: StaffDetail[] = fullGCGRRes.data?.gcgr_staff_details || [];
  if (staffData) {
    const phasesData: Record<string, any>[] = fullGCGRRes.data?.gcgr_phases || [];
    const employeesRes = await fetchEmployees();
    if (!employeesRes.success) {
      throw employeesRes.error;
    }
    const employeesData: Record<string, any>[] = employeesRes.data || [];
    const positionsRes = await fetchPositions();
    if (!positionsRes.success) {
      throw positionsRes.error;
    }
    const positionsData: Record<string, any>[] = positionsRes.data || [];
    await tabulateStaffDetail(staffData, phasesData, employeesData, positionsData, gcgr);
  }

  //tabulate line item detail
  const lineItemsData = fullGCGRRes.data?.gcgr_detail_line_items || [];
  if (lineItemsData) {
    const groupsRes = await fetchGCGRDetailGroups();
    if (!groupsRes.success) {
      throw groupsRes.error;
    }
    const groupsData: Record<string, any>[] = groupsRes.data || [];
    await tabulateGcGrLineItems(lineItemsData, groupsData);
  }

  //tabulate gr labor
  if (gcgr.labor_rates) {
    const laborRes = await fetchGCGRNonstaffLaborTypes();
    if (!laborRes.success) {
      throw laborRes.error;
    }
    const laborData: Record<string, any>[] = laborRes.data || [];
    const nonStaffDetailsData = fullGCGRRes.data?.gcgr_nonstaff_details || [];
    const crewData = fullGCGRRes.data?.gcgr_nonstaff_detail_crews || [];
    await tabulateGrLabor(laborData, nonStaffDetailsData, crewData, staffData, gcgr);
  } else {
    // Selecting a GCGR without labor rates must not leave the previous selection's rows standing.
    await loadDataIntoSheet([], "GRLabor");
  }

  const endTime = performance.now();
  const totalDuration = (endTime - startTime) / 1000;
  track("excel.gcgr.load", { loadTime: totalDuration });
};

const tabulateGrLabor = async (
  laborTypes: Record<string, any>[],
  nonStaffDetails: Record<string, any>[],
  crewDetails: Record<string, any>[],
  staffDetails: StaffDetail[],
  gcgr: GCGR
) => {
  try {
    // get latest date: max of construction_finish, latest staff finish, and last non-staff allocation week
    // (mirrors getGCGRConstructionWeeksArray in common/utils/gcgr.js)
    const constructionFinish = new Date(gcgr.construction_finish + "T00:00:00");
    const constructionStart = new Date(gcgr.construction_start + "T00:00:00");

    const latestStaffFinish = staffDetails.reduce((max, s) => {
      const finish = new Date(s.allocations.finish_date + "T00:00:00");
      return finish > max ? finish : max;
    }, constructionFinish);

    const latestNonStaffFinish = nonStaffDetails.reduce((max, detail) => {
      const lastIndex = detail.allocations.findLastIndex((a: Record<string, any>) => a && Object.keys(a).length > 0);
      if (lastIndex < 0) return max;
      const lastDate = new Date(constructionStart);
      lastDate.setDate(lastDate.getDate() + lastIndex * 7);
      return lastDate > max ? lastDate : max;
    }, new Date(constructionStart));

    const latestDate = [constructionFinish, latestStaffFinish, latestNonStaffFinish].reduce((max, d) =>
      d > max ? d : max
    );

    // get used labor types from gcgr parent object
    const laborRates = Object.entries(gcgr.labor_rates ?? {}).flatMap(([id, rates]) =>
      Object.entries(rates ?? {})
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .map(([date, data], i, arr) => ({
          id,
          rate_from_date: date,
          rate_to_date: arr[i + 1]?.[0] ?? "2077-01-01",
          regular_time_rate: data.rate,
          overtime_rate: data.ot_rate,
          double_time_rate: data.dt_rate,
        }))
    );

    // match non staff details with the week they are associated with
    const nonStaffDetailsProcessed = nonStaffDetails.flatMap((d) =>
      d.allocations.map((alloc: Record<string, any>, index: number) => ({
        id: d.id,
        name: d.name,
        week_number: index + 1,
        total: alloc.total,
        crew_guid: alloc.crewId,
      }))
    );

    // flatten crew comps
    const crewDetailsProcessed = crewDetails.flatMap((c) => {
      if (typeof c.composition === "object" && c.composition !== null) {
        return Object.entries(c.composition)
          .map(([crewId, comp]) => {
            if (
              typeof comp === "object" &&
              comp !== null &&
              "regularTime" in comp &&
              "overtime" in comp &&
              "doubleTime" in comp
            ) {
              return {
                id: c.id,
                crew_id: crewId,
                crew_name: c.name,
                crew_key: c.key,
                regularTime: (comp as { regularTime: number }).regularTime,
                overtime: (comp as { overtime: number }).overtime,
                doubleTime: (comp as { doubleTime: number }).doubleTime,
              };
            }
            return null; // Skip invalid entries
          })
          .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
      }
      return []; // Return empty array if composition is not valid
    });

    // get weekly range array
    const weeks = [];
    // Get the Monday of the week containing construction_start
    let currentDate = new Date(gcgr.construction_start + "T00:00:00"); // Parse as local time
    const day = currentDate.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const diffToMonday = (day + 6) % 7; // Adjusts Sunday (0) to -6, Monday (1) to 0, etc.
    currentDate.setDate(currentDate.getDate() - diffToMonday); // Back up to Monday
    while (currentDate <= latestDate) {
      weeks.push({
        week: new Date(currentDate),
        week_end: new Date(currentDate.getTime() + 6 * 24 * 60 * 60 * 1000), // Sunday
      });
      currentDate.setDate(currentDate.getDate() + 7);
    }
    // get the matching labor type and labor rates for each week
    const rateWeeks: Array<Record<string, any>> = [];
    weeks.forEach((week, index) => {
      laborRates.forEach((rate) => {
        if (new Date(rate.rate_from_date) <= week.week && new Date(rate.rate_to_date) >= week.week) {
          const laborType = laborTypes.find((lt) => lt.id === rate.id);
          if (laborType) {
            rateWeeks.push({
              week_number: index + 1,
              week: week.week,
              week_end: week.week_end,
              labor_type_id: rate.id,
              name: laborType.name,
              regular_time_rate: rate.regular_time_rate,
              overtime_rate: rate.overtime_rate,
              double_time_rate: rate.double_time_rate,
            });
          }
        }
      });
    });

    // combine data into one result array
    const results: Array<Record<string, any>> = [];
    rateWeeks.forEach((rw) => {
      crewDetailsProcessed.forEach((cd) => {
        if (cd.crew_id === rw.labor_type_id) {
          nonStaffDetailsProcessed.forEach((nd) => {
            if (nd.week_number === rw.week_number && nd.crew_guid === cd.id) {
              const regularTimeHours = cd.regularTime * 40;
              const overtimeHours = cd.overtime * 40;
              const doubleTimeHours = cd.doubleTime * 40;
              const totalHours = regularTimeHours + overtimeHours + doubleTimeHours;
              const regularTimeCost = rw.regular_time_rate * regularTimeHours;
              const overtimeCost = rw.overtime_rate * overtimeHours;
              const doubleTimeCost = rw.double_time_rate * doubleTimeHours;
              // nd.total is a manual override; a week without one arrives as null/undefined (not 0),
              // so fall back to the computed cost only when no override is present.
              const totalCost = nd.total > 0 ? nd.total : regularTimeCost + overtimeCost + doubleTimeCost;
              results.push({
                labor_category: nd.name,
                crew_name: cd.crew_name,
                crew_key: cd.crew_key,
                name: rw.name,
                week_number: rw.week_number,
                week_date: rw.week.toLocaleDateString(),
                regular_time_hours: regularTimeHours,
                overtime_hours: overtimeHours,
                double_time_hours: doubleTimeHours,
                total_hours: totalHours,
                regular_time_rate: rw.regular_time_rate,
                overtime_rate: rw.overtime_rate,
                double_time_rate: rw.double_time_rate,
                regular_time_cost: regularTimeCost,
                overtime_cost: overtimeCost,
                double_time_cost: doubleTimeCost,
                total_cost: totalCost,
              });
            }
          });
        }
      });
    });

    // group results by labor category and then sort by week number
    results.sort((a, b) => {
      const categoryComparison = a.labor_category.localeCompare(b.labor_category);
      if (categoryComparison !== 0) return categoryComparison;
      return a.week_number - b.week_number;
    });

    await loadDataIntoSheet(results, "GRLabor", { dateOnly: true });
  } catch (error) {
    Logger.warn("Error with GRLabor etl", serializeErrorForWarning(error));
  }
};

const tabulateStaffDetail = async (
  staffing: StaffDetail[],
  phases: Record<string, any>[],
  employees: Record<string, any>[],
  positions: Record<string, any>[],
  gcgr: GCGR
) => {
  try {
    const records = transformStaffDetails(staffing, phases, employees, positions, gcgr);
    await loadDataIntoSheet(records, "GCGRStaffing", { sizeColumns: true, dateOnly: true });
  } catch (error) {
    Logger.warn("Error with staff detail etl", serializeErrorForWarning(error));
  }
};

const tabulateGcGrLineItems = async (lineItems: GCGRLineItem[], groups: Record<string, any>[]) => {
  try {
    const records = transformGcgrLineItems(lineItems, groups);
    await loadDataIntoSheet(records, "GCGRLineItems", { sizeColumns: true });
  } catch (error) {
    Logger.warn("Error with GCGR etl", serializeErrorForWarning(error));
  }
};

/* -------------------------------------------------------------------------- */
/* Contracting                                                               */
/* -------------------------------------------------------------------------- */

export const loadContractingData = async (
  projectId: string
): Promise<{ bidGroups: BidGroup[]; markups: InternalEstimateMarkup[] } | null> => {
  const startTime = performance.now();
  const { bidGroups, markups, scopeUseGroups, scopeAlternates, scopeAlternateBids, scopeTradeCleanupBids, bids } =
    await getBidTabs(projectId);
  await tabulateBidTabs(bidGroups, scopeUseGroups, scopeAlternates, scopeAlternateBids, scopeTradeCleanupBids, bids);
  const endTime = performance.now();
  const totalDuration = (endTime - startTime) / 1000;
  track("excel.bidTabs.load", { loadTime: totalDuration });
  return bidGroups.length > 0 ? { bidGroups, markups } : null;
};

const tabulateBidTabs = async (
  bidGroupsData: BidGroup[],
  scopeUseGroups: ScopeUseGroup[] = [],
  scopeAlternates: ScopeAlternate[] = [],
  scopeAlternateBids: ScopeAlternateBid[] = [],
  scopeTradeCleanupBids: ScopeTradeCleanupBid[] = [],
  bids: Bid[] = []
) => {
  try {
    const tabulatedData = transformBidTabsToFlatRows(
      bidGroupsData,
      scopeUseGroups,
      scopeAlternates,
      scopeAlternateBids,
      scopeTradeCleanupBids,
      bids
    );
    await loadDataIntoSheet(tabulatedData, "BidTabs");
  } catch (error) {
    Logger.warn("Error with BID TABS etl", serializeErrorForWarning(error));
  }
};
